import React, { useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { getIn } from 'formik';
import { Checkbox } from '../../../..';
import { FilterDropDown } from '../../FilterDropdown/FilterDropdown';
import { CaretRightOutlined } from '@ant-design/icons';

import styles from './index.module.css';

const NestedOptions = ({ renderedValues, values, name, parentChecked, setFieldValue }) => {
  const [expandedParents, setExpandedParents] = useState({});

  const handleParentCheckbox = (parentValue) => ({ checked }) => {
    const existingValues = getIn(values, name) || [];
    let newValues = [];

    if (checked) {
      // Add parent and all children
      const childrenIds = parentValue.children?.map(child => child.value) || [];
      newValues = [...existingValues, parentValue.value, ...childrenIds];
      // Expand the parent when checked
      setExpandedParents(prev => ({
        ...prev,
        [parentValue.value.id]: true
      }));
    } else {
      // Remove parent and all children
      const parentAndChildrenIds = [
        parentValue.value.id,
        ...(parentValue.children?.map(child => child.value.id) || [])
      ];
      newValues = existingValues.filter(value => !parentAndChildrenIds.includes(value.id));
    }

    setFieldValue(name, newValues);
  };

  const handleChildCheckbox = (parentValue, childValue) => ({ checked }) => {
    const existingValues = getIn(values, name) || [];
    let newValues = [];

    if (checked) {
      newValues = [...existingValues, childValue.value];
      
      // Check if all siblings are checked to determine if parent should be checked
      const allChildrenChecked = parentValue.children?.every(child =>
        [...newValues].some(v => v.id === child.value.id)
      );

      if (allChildrenChecked) {
        newValues = [...newValues, parentValue.value];
      }
    } else {
      // Remove child and parent (if it exists)
      newValues = existingValues.filter(value => 
        value.id !== childValue.value.id && value.id !== parentValue.value.id
      );
    }

    setFieldValue(name, newValues);
  };

  const toggleExpand = (parentId) => {
    setExpandedParents(prev => ({
      ...prev,
      [parentId]: !prev[parentId]
    }));
  };

  return renderedValues.map((parent) => {
    const hasChildren = parent.children && parent.children.length > 0;
    const parentCheckedState = Boolean(
      getIn(values, name)?.some((v) => v.id === parent.value.id)
    );
    const isExpanded = expandedParents[parent.value.id];
    
    return (
      <div key={parent.value.id} className={styles.parentCheckbox}>
        <div className={styles.parentRow}>
          {hasChildren && (
            <CaretRightOutlined
              className={classnames(styles.expandIcon, {
                [styles.expanded]: isExpanded
              })}
              onClick={() => toggleExpand(parent.value.id)}
            />
          )}
          <label className={styles.checkbox}>
            <Checkbox
              name={name}
              checked={parentCheckedState}
              value={parent.value}
              onChange={handleParentCheckbox(parent)}
              indeterminate={hasChildren && !parentCheckedState && 
                parent.children.some(child => 
                  getIn(values, name)?.some(v => v.id === child.value.id)
                )}
            />
            <span className={styles.checkboxLabel}>{parent.title}</span>
          </label>
        </div>
        
        {hasChildren && isExpanded && (
          <div className={styles.childrenContainer}>
            {parent.children.map((child) => {
              const childChecked = Boolean(
                getIn(values, name)?.some((v) => v.id === child.value.id)
              );

              return (
                <label key={child.value.id} className={styles.checkbox}>
                  <Checkbox
                    name={name}
                    checked={childChecked}
                    value={child.value}
                    onChange={handleChildCheckbox(parent, child)}
                  />
                  <span className={styles.checkboxLabel}>{child.title}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  });
};

export const NestedCheckboxDropDown = ({
  Component,
  name,
  options,
  values,
  setFieldValue,
  shouldRenderSorting
}) => {
  const [visible, setVisible] = useState(false);

  return (
    <th className='ant-table-cell'>
      <FilterDropDown
        visible={visible}
        content={
          <div className={styles.dropdownContent}>
            <NestedOptions
              renderedValues={options}
              values={values}
              name={name}
              setFieldValue={setFieldValue}
            />
          </div>
        }
        name={name}
        values={values}
        setFieldValue={setFieldValue}
        shouldRenderSorting={shouldRenderSorting}
        onVisibleChange={setVisible}
      >
        <Component visible={visible} />
      </FilterDropDown>
    </th>
  );
};

NestedCheckboxDropDown.propTypes = {
  Component: PropTypes.elementType,
  name: PropTypes.string,
  options: PropTypes.array,
  values: PropTypes.object,
  setFieldValue: PropTypes.func,
  shouldRenderSorting: PropTypes.bool
};